import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

interface Contact {
  id: string;
  name: string;
  first_name: string;
  last_name?: string;
  function?: string;
}

interface ContactSelectorProps {
  companyId: string;
  onSelectContact: (contactId: string) => void;
  selectedContactId?: string;
  disabled?: boolean; // Added the missing disabled prop
}

export const ContactSelector = ({ companyId, onSelectContact, selectedContactId, disabled = false }: ContactSelectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Fetch contacts based on company ID and search term
  useEffect(() => {
    const fetchContacts = async () => {
      if (open && companyId) {
        setIsLoading(true);
        setError(null);
        
        try {
          let query = supabase
            .from('contacts')
            .select('id, name, first_name, last_name, function')
            .eq('company_id', companyId);
          
          // Add search filter if there's a search term
          if (searchTerm) {
            query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`);
          }
          
          const { data, error } = await query.limit(10);
          
          if (error) {
            console.error("Error fetching contacts:", error);
            setError("Failed to load contacts");
            setContacts([]);
            return;
          }
          
          setContacts(data || []);
        } catch (error) {
          console.error("Failed to fetch contacts:", error);
          setError("An unexpected error occurred");
          setContacts([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchContacts();
  }, [searchTerm, open, companyId]);

  // Fetch selected contact details when selectedContactId changes
  useEffect(() => {
    const fetchSelectedContact = async () => {
      if (selectedContactId) {
        try {
          const { data, error } = await supabase
            .from('contacts')
            .select('id, name, first_name, last_name, function')
            .eq('id', selectedContactId)
            .single();
          
          if (error) {
            console.error("Error fetching selected contact:", error);
            return;
          }
          
          if (data) {
            setSelectedContact(data);
            setSearchTerm(data.name);
          }
        } catch (error) {
          console.error("Failed to fetch selected contact:", error);
        }
      }
    };

    if (!selectedContact && selectedContactId) {
      fetchSelectedContact();
    }
  }, [selectedContactId, selectedContact]);

  // Clear selected contact when companyId changes
  useEffect(() => {
    if (!selectedContactId) {
      setSelectedContact(null);
      setSearchTerm("");
    }
  }, [selectedContactId]);

  const getDisplayName = (contact: Contact) => {
    return contact.name || `${contact.first_name} ${contact.last_name || ''}`.trim();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedContact ? getDisplayName(selectedContact) : t("companySelector.selectContactPlaceholder")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: triggerRef.current ? triggerRef.current.offsetWidth : undefined }}>
        <Command>
          <CommandInput 
            placeholder={t("companySelector.searchContactPlaceholder", "Search contacts...")}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Loading contacts...
              </div>
            ) : error ? (
              <div className="py-6 text-center text-sm text-red-500">
                {error}
              </div>
            ) : (
              <>
                <CommandEmpty>{t("companySelector.noContactsFound", "No contacts found for this company.")}</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-auto">
                  {contacts.map((contact) => (
                    <CommandItem
                      key={contact.id}
                      value={contact.name}
                      onSelect={() => {
                        onSelectContact(contact.id);
                        setSelectedContact(contact);
                        setSearchTerm(getDisplayName(contact));
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedContactId === contact.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{getDisplayName(contact)}</span>
                        {contact.function && (
                          <span className="text-xs text-gray-500">{contact.function}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
